import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { AccessLevel } from '../types';

/**
 * What the signed-in user is allowed to do.
 *
 * This drives what the UI offers. The same rules are enforced in school-service,
 * so hiding a button is a courtesy rather than the actual protection.
 */
export interface Access {
  level: AccessLevel;
  /** Channel the user is confined to, or null when they see every channel. */
  channel: string | null;
  /** May create, edit or delete. */
  canWrite: boolean;
  /** Sees more than a single channel, so channel-level views are meaningful. */
  canSeeChannels: boolean;
}

export const useAccess = (): Access => {
  const user = useSelector((state: RootState) => state.auth.user);

  // Absent claims mean an older token; treat as full access, matching the backend default.
  const level = (user?.accessLevel as AccessLevel) ?? AccessLevel.FULL;
  const isChannelUser = level === AccessLevel.CHANNEL;

  return {
    level,
    channel: isChannelUser ? user?.channel ?? null : null,
    canWrite: level !== AccessLevel.READ_ONLY,
    canSeeChannels: !isChannelUser,
  };
};

export default useAccess;
